import { useEffect, useRef, useState } from "react";
import { streamChat } from "../lib/streaming";
import { chatUrl } from "./CanvasFlow";
import { renderProse } from "./prose";
import { useScoringModel } from "./scoringModel";
import { buildDRSystemPrompt, buildSystemPrompt, parseEvalTail } from "./prompt";
import { useConfirm } from "../ui/ConfirmDialog";
import {
  advanceReviewLayer,
  cancelReview,
  editReviewField,
  getCanvas,
  getReview,
} from "./api";
import {
  DR_QUESTIONS,
  findPhase,
  PHASES,
  type CanvasSession,
  type FieldAnswer,
  type PhaseCode,
  type ReviewLayer,
  type ReviewState,
} from "./schema";

interface Props {
  sessionId: string;
  onCancel: () => void;
  onReadyToFinalize: () => void;
}

const REVIEW_QUESTIONS: Record<ReviewLayer, string> = {
  S1: "Has your intent changed? Are the same human capabilities still irreplaceable? Has anyone left the team, or has the agent's scope expanded?",
  S2: "Are the success criteria still the right ones? Has the audience changed? Have new failure modes emerged?",
  S3: "Are all constraints still enforced? Has access scope changed? Any new regulatory requirements? Has the safety owner changed?",
  S4: "Are decision rights still correct? Has the override rate suggested any recalibration? Any new decision types the agent handles?",
  S5: "Have any triggers fired since last review? Were the responses appropriate? Is the manual fallback still viable?",
  S6: "What does the Five-Number Panel show over the last quarter? Any drift signals?",
  DR: "Are the deployment-readiness answers still true? Same people watching the same signals, same pause authority, same escalation order?",
};

// A normalized item the wizard renders, whether it comes from a lettered phase
// (draftPhases) or the deployment-readiness step (draftDR).
interface ReviewItem {
  name: string;
  code?: string;
  label: string;
  question: string;
  current: FieldAnswer | undefined;
}

export function ReviewWizard({ sessionId, onCancel, onReadyToFinalize }: Props) {
  const confirm = useConfirm();
  const [review, setReview] = useState<ReviewState | null>(null);
  const [session, setSession] = useState<CanvasSession | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, s] = await Promise.all([
          getReview(sessionId),
          getCanvas(sessionId),
        ]);
        if (cancelled) return;
        setReview(r?.review ?? null);
        setSession(s);
        if (!r?.review) {
          setError("No active review for this canvas.");
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (error && !review) {
    return (
      <div className="canvas">
        <div className="canvas__panel">
          <div className="banner banner--error">{error}</div>
          <footer className="canvas__actions">
            <button className="btn btn--ghost" onClick={onCancel}>← Home</button>
          </footer>
        </div>
      </div>
    );
  }
  if (!review || !session) {
    return <div className="canvas canvas--loading">Loading review…</div>;
  }

  const isDR = review.currentLayer === "DR";
  const phase = isDR ? null : findPhase(review.currentLayer as PhaseCode);
  const layerTitle = isDR ? "Deployment Readiness" : phase!.title;
  const layerSubtitle = isDR
    ? "Re-confirm the readiness answers"
    : phase!.subtitle;
  // DR is the final review layer (S1…S6 → DR → finalize).
  const isLastLayer = isDR;

  const items: ReviewItem[] = isDR
    ? DR_QUESTIONS.map((dr) => ({
        name: dr.code,
        label: dr.code,
        question: dr.question,
        current: review.draftDR?.answers[dr.code],
      }))
    : phase!.fields.map((field) => ({
        name: field.name,
        code: field.code,
        label: field.label,
        question: field.question,
        current: review.draftPhases[review.currentLayer as PhaseCode]?.fields[
          field.name
        ],
      }));

  const handleAdvance = async () => {
    setAdvancing(true);
    setError(null);
    try {
      const r = await advanceReviewLayer(sessionId, review.currentLayer);
      if (r.readyToFinalize) {
        onReadyToFinalize();
      } else {
        setReview(r.review);
        setEditingField(null);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAdvancing(false);
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: "Cancel this review?",
      message: "Any edits you've made in this review will be discarded. The current canvas version stays unchanged.",
      confirmLabel: "Discard edits",
      cancelLabel: "Keep editing",
      danger: true,
    });
    if (!ok) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelReview(sessionId);
      onCancel();
    } catch (e) {
      setError((e as Error).message);
      setCancelling(false);
    }
  };

  return (
    <div className="canvas">
      <div className="canvas__panel">
        <header className="canvas__header">
          <div className="canvas__meta">
            <span className="canvas__phase-code">{review.currentLayer}</span>
            <span className="canvas__phase-title">— {layerTitle}</span>
          </div>
          <div className="canvas__progress">
            <span className="mono">Phase 10 review · parent v{review.parentVersion}</span>
          </div>
        </header>

        <p className="canvas__eyebrow">Review — {layerTitle}</p>
        <h1 className="canvas__display">{layerSubtitle}</h1>
        <p className="canvas__lede">{REVIEW_QUESTIONS[review.currentLayer]}</p>

        {error && <div className="banner banner--error">{error}</div>}

        <div className="review__fields">
          {items.map((item) => {
            const current = item.current;
            const isEditing = editingField === item.name;
            return (
              <article key={item.name} className="review__field">
                <header className="review__field-head">
                  <div className="review__field-label">
                    {item.code ? <><span className="mono">{item.code}</span> · {item.label}</> : item.label}
                  </div>
                  {!isEditing && (
                    <button
                      className="btn btn--ghost"
                      onClick={() => setEditingField(item.name)}
                      disabled={advancing || cancelling || editingField !== null}
                    >
                      Edit
                    </button>
                  )}
                </header>
                {!isEditing ? (
                  <>
                    <p className="review__field-q">{renderProse(item.question)}</p>
                    <div className="review__field-answer">
                      {current?.answer ? (
                        current.answer
                      ) : (
                        <em>No answer yet.</em>
                      )}
                    </div>
                    <div className="review__field-meta">
                      <span className="mono">
                        Score {current?.score ?? "—"}/5
                      </span>
                      {current?.gapFlagged && (
                        <span className="chip chip--warn">
                          <span className="chip__k">Gap</span>
                          <span className="chip__v">
                            {current.gapNote || "Flagged"}
                          </span>
                        </span>
                      )}
                      {current?.confidence && (
                        <span className="chip chip--neutral">
                          <span className="chip__k">Confidence</span>
                          <span className="chip__v mono">{current.confidence}</span>
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <EditField
                    sessionId={sessionId}
                    session={session}
                    code={review.currentLayer}
                    fieldName={item.name}
                    question={item.question}
                    isDR={isDR}
                    initialAnswer={current?.answer ?? ""}
                    onSave={(updated) => {
                      setReview((r) => {
                        if (!r) return r;
                        if (r.currentLayer === "DR") {
                          const prevDR = r.draftDR ?? {
                            status: "complete" as const,
                            answers: {},
                          };
                          return {
                            ...r,
                            draftDR: {
                              ...prevDR,
                              answers: {
                                ...prevDR.answers,
                                [item.name]: updated,
                              },
                            },
                          };
                        }
                        const code = r.currentLayer as PhaseCode;
                        return {
                          ...r,
                          draftPhases: {
                            ...r.draftPhases,
                            [code]: {
                              ...r.draftPhases[code],
                              fields: {
                                ...r.draftPhases[code].fields,
                                [item.name]: updated,
                              },
                            },
                          },
                        };
                      });
                      setEditingField(null);
                    }}
                    onCancel={() => setEditingField(null)}
                  />
                )}
              </article>
            );
          })}
        </div>

        <footer className="canvas__actions">
          <button
            className="btn btn--text btn--danger"
            onClick={() => void handleCancel()}
            disabled={advancing || cancelling || editingField !== null}
          >
            {cancelling ? "Cancelling…" : "Cancel review"}
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void handleAdvance()}
            disabled={advancing || cancelling || editingField !== null}
          >
            {advancing
              ? "Saving…"
              : isLastLayer
                ? "Continue to delta report →"
                : "Save & continue →"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── EditField — reuses the same streamChat + buildSystemPrompt + parseEvalTail
//    pipeline as CanvasFlow's per-field eval. Re-scoring during review uses
//    Pass-1 semantics only (no pushback escalation) per spec §5 risk #3.

interface EditFieldProps {
  sessionId: string;
  session: CanvasSession;
  code: ReviewLayer;
  fieldName: string;
  question: string;
  isDR: boolean;
  initialAnswer: string;
  onSave: (updated: FieldAnswer) => void;
  onCancel: () => void;
}

type EditPhase = "ask" | "streaming" | "evaluated" | "saving";

function EditField({
  sessionId,
  session,
  code,
  fieldName,
  question,
  isDR,
  initialAnswer,
  onSave,
  onCancel,
}: EditFieldProps) {
  const scoringModel = useScoringModel();
  const [answer, setAnswer] = useState(initialAnswer);
  const [streamText, setStreamText] = useState("");
  const [evalParsed, setEvalParsed] =
    useState<ReturnType<typeof parseEvalTail>>(null);
  const [phase, setPhase] = useState<EditPhase>("ask");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // For lettered phases, look up the field's positional index so
  // buildSystemPrompt produces the same prompt CanvasFlow uses for the live
  // field. DR uses its own prompt builder and has no phase index.
  const fieldIndex = isDR
    ? -1
    : findPhase(code as PhaseCode).fields.findIndex((f) => f.name === fieldName);

  const startStream = () => {
    if (!answer.trim() || phase === "streaming") return;
    // Re-eval uses Pass-1 semantics (pushbackPass = 0 → "initial ask", no
    // pushback block emitted). Matches spec §5 risk #3.
    const system = isDR
      ? buildDRSystemPrompt({
          drCode: fieldName,
          drQuestion: question,
          session,
          pushbackPass: 0,
        })
      : buildSystemPrompt({
          phase: code as PhaseCode,
          fieldIndex,
          session,
          pushbackPass: 0,
        });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStreamText("");
    setEvalParsed(null);
    setError(null);
    setPhase("streaming");

    let acc = "";
    streamChat({
      url: chatUrl(),
      sessionId,
      system,
      messages: [
        {
          role: "user",
          content: isDR
            ? `Step: ${fieldName}\nUser answer:\n"""\n${answer.trim()}\n"""`
            : `Field: ${code}.${fieldName}\n` +
              `User answer:\n"""\n${answer.trim()}\n"""`,
        },
      ],
      provider: "anthropic",
      model: scoringModel,
      signal: ctrl.signal,
      onText: (delta) => {
        acc += delta;
        const tailIdx = acc.indexOf("<<<JSON>>>");
        setStreamText(tailIdx >= 0 ? acc.slice(0, tailIdx) : acc);
      },
      onDone: () => {
        const parsed = parseEvalTail(acc);
        if (!parsed) {
          setError("Could not parse evaluation. Try refining.");
          setPhase("ask");
          return;
        }
        setStreamText(parsed.proseText);
        setEvalParsed(parsed);
        setPhase("evaluated");
      },
      onError: (e) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setPhase("ask");
      },
    });
  };

  const handleSave = async () => {
    if (!evalParsed) return;
    setPhase("saving");
    setError(null);
    try {
      const res = await editReviewField(sessionId, code, {
        fieldName,
        answer: answer.trim(),
        score: evalParsed.score,
        gapFlagged: evalParsed.gapFlagged,
        gapNote: evalParsed.gapNote,
        confidence: evalParsed.confidence,
        evalText: evalParsed.proseText,
        scoringModel,
      });
      onSave(res.field);
    } catch (e) {
      setError((e as Error).message);
      setPhase("evaluated");
    }
  };

  const busy = phase === "streaming" || phase === "saving";

  return (
    <div className="review__editor">
      <p className="review__field-q">{renderProse(question)}</p>
      <textarea
        className="composer__textarea"
        rows={6}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={busy}
        placeholder="Revise the answer with new specifics — owners, mechanisms, cadences."
      />
      {(phase === "streaming" || phase === "evaluated") && (
        <section className="eval">
          <header className="eval__header">
            <span className="eval__label">Coach evaluation</span>
            {evalParsed && (
              <span className="eval__score">
                <span className="mono">{evalParsed.score}</span>
                <span className="eval__score-of">/5</span>
              </span>
            )}
          </header>
          <p className="eval__text">
            {renderProse(streamText)}
            {phase === "streaming" && <span className="caret" />}
          </p>
          {evalParsed && (
            <div className="eval__chips">
              <span className="chip chip--neutral">
                <span className="chip__k">Confidence</span>
                <span className="chip__v mono">{evalParsed.confidence}</span>
              </span>
              {evalParsed.gapFlagged ? (
                <span className="chip chip--warn">
                  <span className="chip__k">Gap</span>
                  <span className="chip__v">
                    {evalParsed.gapNote || "Flagged"}
                  </span>
                </span>
              ) : (
                <span className="chip chip--ok">
                  <span className="chip__k">Gap</span>
                  <span className="chip__v">None</span>
                </span>
              )}
            </div>
          )}
        </section>
      )}
      {error && <div className="banner banner--error">{error}</div>}
      <div className="review__editor-actions">
        <button className="btn btn--text" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {phase === "ask" && (
          <button
            className="btn btn--primary"
            onClick={() => startStream()}
            disabled={!answer.trim()}
          >
            Re-evaluate →
          </button>
        )}
        {phase === "streaming" && (
          <button
            className="btn btn--ghost"
            onClick={() => abortRef.current?.abort()}
          >
            Stop
          </button>
        )}
        {phase === "evaluated" && (
          <>
            <button
              className="btn btn--ghost"
              onClick={() => {
                setStreamText("");
                setEvalParsed(null);
                setPhase("ask");
              }}
            >
              Refine
            </button>
            <button
              className="btn btn--primary"
              onClick={() => void handleSave()}
            >
              Save edit
            </button>
          </>
        )}
        {phase === "saving" && (
          <button className="btn btn--primary" disabled>
            Saving…
          </button>
        )}
      </div>
    </div>
  );
}

// silence unused import warning when PHASES isn't referenced via findPhase only
void PHASES;
